import { Injector } from '@angular/core';
import DOMPurify from 'dompurify';
import { BlockEmbed } from 'quill/blots/block';
import { EmbedBlot } from 'quill/node_modules/parchment/dist/parchment';
import { FetchImageService } from './fetch-image.service';
import { RichTextEditorConfigService } from './rich-text-editor-config.service';

export class MentionBlot extends EmbedBlot {
    static blotName = 'mention';
    static tagName = 'span';
    static className = 'mention';

    static create(data: any) {
        const node = super.create(data) as HTMLElement;
        const denotationChar = document.createElement('span');
        denotationChar.className = 'ql-mention-denotation-char';
        denotationChar.textContent = data.denotationChar;
        node.appendChild(denotationChar);
        node.appendChild(document.createTextNode(data.value));
        return MentionBlot.setDataValues(node, data);
    }

    static setDataValues(element: any, data: any) {
        const domNode = element;
        Object.keys(data).forEach(key => {
            domNode.dataset[key] = data[key];
        });
        return domNode;
    }

    static value(domNode: any) {
        return domNode.dataset;
    }
}

// ─── Inlined quill-mention: constants/keys ───────────────────────────────────
const Keys = {
    TAB: 9,
    ENTER: 13,
    ESCAPE: 27,
    UP: 38,
    DOWN: 40
};

// ─── Inlined quill-mention: imports/numberisnan ──────────────────────────────
function numberIsNaN(x: any): boolean {
    return x !== x;
}

// ─── Inlined quill-mention: quill.mention.js (Mention class) ─────────────────
export class Mention {
    isOpen: boolean;
    itemIndex: number;
    mentionCharPos: number | null;
    cursorPos: number | null;
    values: any[];
    suspendMouseEnter: boolean;
    quill: any;
    options: any;
    mentionContainer: HTMLElement;
    mentionList: HTMLUListElement;
    mentionChar: string | null;
    searchTerm: string | null;
    lastItemsFromLoad: boolean;

    constructor(quill: any, options: any) {
        this.isOpen = false;
        this.itemIndex = 0;
        this.mentionCharPos = null;
        this.cursorPos = null;
        this.values = [];
        this.suspendMouseEnter = false;

        this.quill = quill;

        this.options = {
            source: null,
            renderItem(item: any, searchTerm: any) {
                return `${item.value}`;
            },
            onSelect(item: any, insertItem: (item: any) => void) {
                insertItem(item);
            },
            mentionDenotationChars: ['@'],
            showDenotationChar: true,
            allowedChars: /^[a-zA-Z0-9_]*$/,
            minChars: 0,
            maxChars: 31,
            offsetTop: 2,
            offsetLeft: 0,
            isolateCharacter: false,
            fixMentionsToQuill: false,
            defaultMenuOrientation: 'bottom',
            dataAttributes: ['id', 'value', 'denotationChar', 'link', 'target'],
            linkTarget: '_blank',
            onOpen() {
                return true;
            },
            onClose() {
                return true;
            },
            listItemClass: 'ql-mention-list-item',
            mentionContainerClass: 'ql-mention-list-container',
            mentionListClass: 'ql-mention-list',
            spaceAfterInsert: true,
            shouldOpenToBottom: true,
            loadOnScroll: false,
            lazyLoad: null
        };

        Object.assign(this.options, options, {
            dataAttributes: Array.isArray(options.dataAttributes)
                ? this.options.dataAttributes.concat(options.dataAttributes)
                : this.options.dataAttributes
        });

        this.mentionContainer = document.createElement('div');
        this.mentionContainer.className = this.options.mentionContainerClass
            ? this.options.mentionContainerClass
            : '';
        this.mentionContainer.style.cssText =
            'display: none; position: absolute;';
        this.mentionContainer.onmousemove =
            this.onContainerMouseMove.bind(this);

        if (this.options.fixMentionsToQuill) {
            this.mentionContainer.style.width = 'auto';
        }

        this.mentionList = document.createElement('ul');
        if (this.options.loadOnScroll) {
            this.mentionList.onscroll = this.onLazyLoading.bind(this);
        }
        this.mentionList.className = this.options.mentionListClass
            ? this.options.mentionListClass
            : '';
        this.mentionContainer.appendChild(this.mentionList);

        this.quill.container.appendChild(this.mentionContainer);

        quill.on('text-change', this.onTextChange.bind(this));
        quill.on('selection-change', this.onSelectionChange.bind(this));

        quill.keyboard.addBinding(
            { key: Keys.TAB },
            this.selectHandler.bind(this)
        );
        quill.keyboard.bindings[9].unshift(quill.keyboard.bindings[9].pop());

        quill.keyboard.addBinding(
            { key: Keys.ENTER },
            this.selectHandler.bind(this)
        );
        quill.keyboard.bindings[13].unshift(quill.keyboard.bindings[13].pop());

        quill.keyboard.addBinding(
            { key: Keys.ESCAPE },
            this.escapeHandler.bind(this)
        );
        quill.keyboard.addBinding({ key: Keys.UP }, this.upHandler.bind(this));
        quill.keyboard.addBinding(
            { key: Keys.DOWN },
            this.downHandler.bind(this)
        );
        this.mentionChar = null;
        this.searchTerm = '';
        this.lastItemsFromLoad = false;
    }

    selectHandler() {
        if (this.isOpen) {
            this.selectItem();
            return false;
        }
        return true;
    }

    escapeHandler() {
        if (this.isOpen) {
            this.hideMentionList();
            return false;
        }
        return true;
    }

    upHandler() {
        if (this.isOpen) {
            this.prevItem();
            return false;
        }
        return true;
    }

    downHandler() {
        if (this.isOpen) {
            this.nextItem();
            return false;
        }
        return true;
    }

    showMentionList() {
        this.mentionContainer.style.visibility = 'hidden';
        this.mentionContainer.style.display = '';
        this.setMentionContainerPosition();
        this.setIsOpen(true);
    }

    hideMentionList() {
        this.mentionContainer.style.display = 'none';
        this.setIsOpen(false);
    }

    highlightItem(scrollItemInView = true) {
        for (let i = 0; i < this.mentionList.childNodes.length; i += 1) {
            (this.mentionList.childNodes[i] as HTMLElement).classList.remove(
                'selected'
            );
        }
        (
            this.mentionList.childNodes[this.itemIndex] as HTMLElement
        ).classList.add('selected');

        if (scrollItemInView) {
            const itemHeight = (
                this.mentionList.childNodes[this.itemIndex] as HTMLElement
            ).offsetHeight;
            const itemPos = this.itemIndex * itemHeight;
            const containerTop = this.mentionContainer.scrollTop;
            const containerBottom =
                containerTop + this.mentionContainer.offsetHeight;

            if (itemPos < containerTop) {
                this.mentionContainer.scrollTop = itemPos;
            } else if (itemPos > containerBottom - itemHeight) {
                this.mentionContainer.scrollTop +=
                    itemPos - containerBottom + itemHeight;
            }
        }
    }

    getItemData() {
        const node = this.mentionList.childNodes[this.itemIndex] as HTMLElement;
        const { link } = (node as any).dataset;
        const hasLinkValue = typeof link !== 'undefined';
        const itemTarget = (node as any).dataset.target;
        if (hasLinkValue) {
            (node as any).dataset.value = `<a href="${link}" target=${
                itemTarget || this.options.linkTarget
            }>${(node as any).dataset.value}`;
        }
        return (node as any).dataset;
    }

    onContainerMouseMove() {
        this.suspendMouseEnter = false;
    }

    selectItem() {
        const data = this.getItemData();
        this.options.onSelect(data, (asyncData: any) => {
            this.insertItem(asyncData);
        });
        this.hideMentionList();
    }

    insertItem(data: any) {
        const render = data;
        if (render === null) {
            return;
        }
        if (!this.options.showDenotationChar) {
            render.denotationChar = '';
        }

        const prevMentionCharPos = this.mentionCharPos!;

        this.quill.deleteText(
            this.mentionCharPos!,
            this.cursorPos! - this.mentionCharPos!,
            'user'
        );
        this.quill.insertEmbed(prevMentionCharPos, 'mention', render, 'user');
        if (this.options.spaceAfterInsert) {
            this.quill.insertText(prevMentionCharPos + 1, ' ', 'user');
            this.quill.setSelection(prevMentionCharPos + 2, 'user');
        } else {
            this.quill.setSelection(prevMentionCharPos + 1, 'user');
        }
        this.hideMentionList();
    }

    onItemMouseEnter(e: MouseEvent) {
        if (this.suspendMouseEnter) {
            return;
        }
        const index = Number((e.target as HTMLElement).dataset['index']);
        if (!numberIsNaN(index) && index !== this.itemIndex) {
            this.itemIndex = index;
            this.highlightItem(false);
        }
    }

    onItemClick(e: MouseEvent) {
        e.stopImmediatePropagation();
        e.preventDefault();
        this.itemIndex = Number(
            (e.currentTarget as HTMLElement).dataset['index']
        );
        this.highlightItem();
        this.selectItem();
    }

    attachDataValues(element: HTMLElement, data: any) {
        const mention = element as any;
        Object.keys(data).forEach(key => {
            if (this.options.dataAttributes.indexOf(key) > -1) {
                mention.dataset[key] = data[key];
            } else {
                delete mention.dataset[key];
            }
        });
        return mention;
    }

    renderList(
        mentionChar: string,
        data: any[],
        searchTerm: string,
        lastItemsFromLoad: boolean
    ) {
        if (data && data.length > 0) {
            this.values = data;
            this.mentionList.innerHTML = '';

            for (let i = 0; i < data.length; i += 1) {
                const li = document.createElement('li');
                li.className = this.options.listItemClass
                    ? this.options.listItemClass
                    : '';
                (li as any).dataset.index = i;
                li.innerHTML = this.options.renderItem(data[i], searchTerm);
                li.onmouseenter = this.onItemMouseEnter.bind(this);
                (li as any).dataset.denotationChar = mentionChar;
                li.onclick = this.onItemClick.bind(this);
                this.mentionList.appendChild(
                    this.attachDataValues(li, data[i])
                );
            }
            this.itemIndex = 0;
            this.highlightItem();
            this.showMentionList();
        } else {
            this.hideMentionList();
        }
        this.lastItemsFromLoad = lastItemsFromLoad;
    }

    appendToList(
        mentionChar: string,
        data: any[],
        searchTerm: string,
        lastItemsFromLoad?: boolean
    ) {
        if (!data || data.length === 0) {
            this.lastItemsFromLoad = true;
            return;
        }
        const startIndex = this.values.length;
        this.values = this.values.concat(data);

        for (let i = 0; i < data.length; i += 1) {
            const li = document.createElement('li');
            li.className = this.options.listItemClass
                ? this.options.listItemClass
                : '';
            (li as any).dataset.index = startIndex + i;
            li.innerHTML = this.options.renderItem(data[i], searchTerm);
            li.onmouseenter = this.onItemMouseEnter.bind(this);
            (li as any).dataset.denotationChar = mentionChar;
            li.onclick = this.onItemClick.bind(this);
            this.mentionList.appendChild(this.attachDataValues(li, data[i]));
        }
        this.lastItemsFromLoad = !!lastItemsFromLoad;
    }

    nextItem() {
        this.itemIndex = (this.itemIndex + 1) % this.values.length;
        this.suspendMouseEnter = true;
        this.highlightItem();
    }

    prevItem() {
        this.itemIndex =
            (this.itemIndex + this.values.length - 1) % this.values.length;
        this.suspendMouseEnter = true;
        this.highlightItem();
    }

    hasValidChars(s: string) {
        return this.options.allowedChars.test(s);
    }

    containerBottomIsNotVisible(topPos: number, containerPos: DOMRect) {
        const mentionContainerBottom =
            topPos + this.mentionContainer.offsetHeight + containerPos.top;
        return mentionContainerBottom > window.pageYOffset + window.innerHeight;
    }

    containerRightIsNotVisible(leftPos: number, containerPos: DOMRect) {
        if (this.options.fixMentionsToQuill) {
            return false;
        }
        const rightPos =
            leftPos + this.mentionContainer.offsetWidth + containerPos.left;
        const browserWidth =
            window.pageXOffset + document.documentElement.clientWidth;
        return rightPos > browserWidth;
    }

    setIsOpen(isOpen: boolean) {
        if (this.isOpen !== isOpen) {
            if (isOpen) {
                this.options.onOpen();
            } else {
                this.options.onClose();
            }
            this.isOpen = isOpen;
        }
    }

    setMentionContainerPosition() {
        const containerPos = this.quill.container.getBoundingClientRect();
        const mentionCharPos = this.quill.getBounds(this.mentionCharPos);
        const containerHeight = this.mentionContainer.offsetHeight;

        let topPos = this.options.offsetTop;
        let leftPos = this.options.offsetLeft;

        if (this.options.fixMentionsToQuill) {
            this.mentionContainer.style.right = `0px`;
        } else {
            leftPos += mentionCharPos.left;
        }

        if (this.containerRightIsNotVisible(leftPos, containerPos)) {
            const containerWidth =
                this.mentionContainer.offsetWidth + this.options.offsetLeft;
            const quillWidth = containerPos.width;
            leftPos = quillWidth - containerWidth;
        }

        if (this.options.defaultMenuOrientation === 'top') {
            if (this.options.fixMentionsToQuill) {
                topPos = -1 * (containerHeight + this.options.offsetTop);
            } else {
                topPos =
                    mentionCharPos.top -
                    (containerHeight + this.options.offsetTop);
            }

            if (topPos + containerPos.top <= 0) {
                let overMentionCharPos = this.options.offsetTop;
                if (this.options.fixMentionsToQuill) {
                    overMentionCharPos += containerPos.height;
                } else {
                    overMentionCharPos += mentionCharPos.bottom;
                }
                topPos = overMentionCharPos;
            }
        } else {
            if (this.options.fixMentionsToQuill) {
                topPos += containerPos.height;
            } else {
                topPos += mentionCharPos.bottom;
            }

            if (
                this.options.shouldOpenToBottom &&
                this.containerBottomIsNotVisible(topPos, containerPos)
            ) {
                let overMentionCharPos = this.options.offsetTop * -1;
                if (!this.options.fixMentionsToQuill) {
                    overMentionCharPos += mentionCharPos.top;
                }
                topPos = overMentionCharPos - containerHeight;
            }
        }

        if (topPos >= 0) {
            this.mentionContainer.classList.add(
                `${this.options.mentionContainerClass}-bottom`
            );
            this.mentionContainer.classList.remove(
                `${this.options.mentionContainerClass}-top`
            );
        } else {
            this.mentionContainer.classList.add(
                `${this.options.mentionContainerClass}-top`
            );
            this.mentionContainer.classList.remove(
                `${this.options.mentionContainerClass}-bottom`
            );
        }

        this.mentionContainer.style.top = `${topPos}px`;
        this.mentionContainer.style.left = `${leftPos}px`;
        this.mentionContainer.style.visibility = 'visible';
    }

    onSomethingChange() {
        const range = this.quill.getSelection();
        if (range == null) return;
        const cursorPos = range.index;
        this.cursorPos = cursorPos;
        const startPos = Math.max(0, cursorPos - this.options.maxChars);
        const beforeCursorPos = this.quill.getText(
            startPos,
            cursorPos - startPos
        );

        let mentionCharIndex = -1;
        let matchedMentionChar = '';

        this.options.mentionDenotationChars.forEach((char: string) => {
            const index = beforeCursorPos.lastIndexOf(char);
            if (index > mentionCharIndex) {
                mentionCharIndex = index;
                matchedMentionChar = char;
            }
        });

        if (mentionCharIndex > -1) {
            if (
                this.options.isolateCharacter &&
                !(
                    mentionCharIndex === 0 ||
                    !!beforeCursorPos[mentionCharIndex - 1].match(/\s/g)
                )
            ) {
                this.hideMentionList();
                return;
            }
            const mentionCharPos =
                cursorPos - (beforeCursorPos.length - mentionCharIndex);
            this.mentionCharPos = mentionCharPos;
            this.searchTerm = beforeCursorPos.substring(
                mentionCharIndex + matchedMentionChar.length
            );
            if (
                this.searchTerm!.length >= this.options.minChars &&
                this.hasValidChars(this.searchTerm!)
            ) {
                this.mentionChar = matchedMentionChar;
                this.options.source(
                    this.searchTerm!,
                    this.renderList.bind(this, this.mentionChar),
                    this.mentionChar!
                );
            } else {
                this.hideMentionList();
            }
        } else {
            this.hideMentionList();
        }
    }

    onLazyLoading(event: Event) {
        const target = event.target as HTMLElement;
        if (
            !this.lastItemsFromLoad &&
            Math.round(target.offsetHeight + target.scrollTop) >=
                target.scrollHeight
        ) {
            event.stopImmediatePropagation();
            event.preventDefault();
            this.options.lazyLoad(
                event,
                this.searchTerm!,
                this.appendToList.bind(this, this.mentionChar!),
                this.mentionChar!
            );
        }
    }

    onTextChange(delta: any, oldDelta: any, source: string) {
        if (source === 'user') {
            this.onSomethingChange();
        }
    }

    onSelectionChange(range: any) {
        if (range && range.length === 0) {
            this.onSomethingChange();
        } else {
            this.hideMentionList();
        }
    }
}

export class CustomImageBlot extends BlockEmbed {
    static blotName = 'customImage';
    static tagName = 'div';
    static className = 'custom-image';

    static injector: Injector;

    static create(value: string) {
        const node = super.create() as HTMLElement;
        node.setAttribute('data-url', value);
        node.innerHTML = 'loading...';

        this.fetchAndRenderImage(node, value);

        return node;
    }

    static value(node: any) {
        return node.getAttribute('data-url');
    }

    static fetchAndRenderImage(node: any, url: string) {
        const fetchImageService = this.injector.get(FetchImageService);

        fetchImageService
            .fetchImage(url)
            .then(imageDataUrl => {
                const img = document.createElement('img');
                if (typeof imageDataUrl === 'string') {
                    img.src = imageDataUrl;
                }
                node.innerHTML = '';
                node.appendChild(img);
            })
            .catch(error => {
                console.error('Error fetching image:', error);
                node.innerHTML = 'load image fail';
            });
    }
}

export class CustomMentionBlot extends MentionBlot {
    static create(data: any) {
        const node = super.create(data);
        if (
            data.denotationChar === '#RPA' ||
            data.denotationChar === '#BATTERY'
        ) {
            node.innerText = `${data.denotationChar} – ${data.value}`;
        } else {
            node.innerText = `${data.denotationChar}${data.value}`;
        }
        return node;
    }
}

export function quillConfigFactory(configService: RichTextEditorConfigService) {
    function imageHandler(this: any) {
        const editor = this.quill;
        const range = editor.getSelection();

        const fileInput = document.createElement('input');
        fileInput.setAttribute('type', 'file');
        fileInput.setAttribute('accept', 'image/*');
        fileInput.click();

        fileInput.onchange = async () => {
            // @ts-ignore
            const file: File = fileInput.files[0];
            if (file) {
                const fetchImageService =
                    CustomImageBlot.injector.get(FetchImageService);
                try {
                    const imageUrl = await fetchImageService.uploadImage(file);
                    if (imageUrl) {
                        editor.insertEmbed(
                            range.index,
                            'customImage',
                            imageUrl,
                            'user'
                        );
                        editor.setSelection(range.index + 1);
                    }
                } catch (error) {
                    console.error('Error uploading image:', error);
                }
            }
        };
    }

    return {
        modules: {
            toolbar: {
                container: [
                    ['bold', 'italic', 'underline', 'strike'],
                    ['image']
                ],
                handlers: {
                    image: imageHandler
                }
            },
            mention: {
                mentionDenotationChars: ['@', '#RPA', '#BATTERY'],
                defaultMenuOrientation: 'top',
                loadOnScroll: true,
                onSelect: function (
                    item: any,
                    insertItem: (item: any) => void
                ) {
                    if (item.id === 'loading') {
                        return false;
                    }
                    insertItem(item);
                    return true;
                },
                onClose: function () {
                    configService.resetPeopleState();
                },
                source: async function (
                    searchTerm: string,
                    renderList: (
                        arg0: any[],
                        arg1: any,
                        lastPage?: boolean
                    ) => void,
                    mentionChar: string
                ) {
                    const sanitizedSearchTerm = DOMPurify.sanitize(searchTerm, {
                        ALLOWED_TAGS: [],
                        ALLOWED_ATTR: []
                    });
                    renderList(
                        [{ value: 'loading...', id: 'loading', loading: true }],
                        sanitizedSearchTerm
                    );
                    let items: any[] = [];
                    let isLastPage = true;
                    if (mentionChar === '@') {
                        items = await configService.suggestPeople(searchTerm);
                        isLastPage = false;
                    } else if (mentionChar === '#RPA') {
                        items = await configService.suggestRpa(searchTerm);
                    } else if (mentionChar === '#BATTERY') {
                        items = await configService.suggestBattery(searchTerm);
                    }
                    renderList(items, searchTerm, isLastPage);
                },
                lazyLoad: async function (
                    event: any,
                    searchTerm: string,
                    appendList: (
                        arg0: any[],
                        arg1: any,
                        lastPage?: boolean
                    ) => void,
                    mentionChar: string
                ) {
                    if (mentionChar !== '@') return;
                    const { items, isLastPage } =
                        await configService.suggestPeopleLazyLoad(searchTerm);
                    if (items.length > 0) {
                        appendList(items, searchTerm, isLastPage);
                    }
                },
                renderItem: function (
                    item: { loading: any; value: any },
                    searchTerm: any
                ) {
                    if (item.loading) {
                        return 'Loading...';
                    }

                    if (typeof item.value === 'string') {
                        return DOMPurify.sanitize(item.value, {
                            ALLOWED_TAGS: [],
                            ALLOWED_ATTR: []
                        });
                    }
                    return item.value || '';
                },
                blotName: 'mention'
            }
        }
    };
}
